import { useEffect, useState } from "react";
import { Container, Row, Col, Breadcrumb, Card, Tabs, Tab, Spinner, Alert, Modal, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { HomeOutlined, StarOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Rate, Avatar, Empty, notification, Popconfirm } from "antd";
import api from "@/api/api";
import formatDate from "@/utils/formatDate";

const MyReviewsPage = () => {
  const [myReviews, setMyReviews] = useState([]);
  const [reviewsForMyTasks, setReviewsForMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("my-reviews");

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ rating: 0, review_text: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const [myReviewsRes, forMyTasksRes] = await Promise.all([
        api.get("/tasks/my-reviews"),
        api.get("/tasks/reviews-for-my-tasks")
      ]);

      if (myReviewsRes.data.success) {
        setMyReviews(myReviewsRes.data.data);
      }

      if (forMyTasksRes.data.success) {
        setReviewsForMyTasks(forMyTasksRes.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Ma'lumotlarni yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    setEditForm({
      rating: review.rating,
      review_text: review.review_text || ""
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingReview(null);
    setEditForm({ rating: 0, review_text: "" });
  };

  const handleUpdateReview = async () => {
    if (!editingReview) return;

    setSubmitting(true);
    try {
      const response = await api.put(
        `/tasks/${editingReview.task.id}/review/${editingReview.id}`,
        editForm
      );

      if (response.data.success) {
        // Update local state
        setMyReviews(myReviews.map(r =>
          r.id === editingReview.id
            ? { ...r, rating: editForm.rating, review_text: editForm.review_text }
            : r
        ));

        handleCloseEditModal();

        notification.success({
          message: "Baholash yangilandi!",
          description: "Sizning bahongiz muvaffaqiyatli yangilandi.",
          duration: 3,
          placement: "topRight"
        });
      } else {
        notification.error({
          message: "Xatolik",
          description: response.data.message || "Yangilashda xatolik yuz berdi",
          duration: 4,
          placement: "topRight"
        });
      }
    } catch (err) {
      notification.error({
        message: "Xatolik",
        description: err.response?.data?.message || err.message || "Yangilashda xatolik yuz berdi",
        duration: 4,
        placement: "topRight"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (review) => {
    try {
      const response = await api.delete(`/tasks/${review.task.id}/review/${review.id}`);

      if (response.data.success) {
        // Remove from local state
        setMyReviews(myReviews.filter(r => r.id !== review.id));

        notification.success({
          message: "Baholash o'chirildi!",
          description: "Sizning bahongiz muvaffaqiyatli o'chirildi.",
          duration: 3,
          placement: "topRight"
        });
      } else {
        notification.error({
          message: "Xatolik",
          description: response.data.message || "O'chirishda xatolik yuz berdi",
          duration: 4,
          placement: "topRight"
        });
      }
    } catch (err) {
      notification.error({
        message: "Xatolik",
        description: err.response?.data?.message || err.message || "O'chirishda xatolik yuz berdi",
        duration: 4,
        placement: "topRight"
      });
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Xatolik</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Mening baholarim</Breadcrumb.Item>
      </Breadcrumb>

      {/* <h2 className="mb-4">Mening baholarim</h2> */}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        {/* Men bergan baholar */}
        <Tab eventKey="my-reviews" title={`Men bergan baholar (${myReviews.length})`}>
          {myReviews.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <Empty description="Siz hali hech qanday baholash yozmadingiz" />
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-3">
              {myReviews.map((review) => (
                <Col key={review.id} xs={12} md={6} lg={4}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <Rate disabled value={review.rating} style={{ fontSize: '18px' }} />
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditClick(review)}
                            title="Tahrirlash"
                          >
                            <EditOutlined />
                          </button>
                          <Popconfirm
                            title="Bahoni o'chirish"
                            description="Haqiqatan ham bu bahoni o'chirmoqchimisiz?"
                            onConfirm={() => handleDeleteReview(review)}
                            okText="Ha"
                            cancelText="Yo'q"
                          >
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="O'chirish"
                            >
                              <DeleteOutlined />
                            </button>
                          </Popconfirm>
                        </div>
                      </div>

                      <h6 className="mb-2">{review.task.title}</h6>

                      {review.review_text && (
                        <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                          "{review.review_text}"
                        </p>
                      )}

                      {review.task.assigned_user && (
                        <div className="d-flex align-items-center mb-2">
                          <Avatar
                            src={review.task.assigned_user.picture}
                            size={28}
                            style={{ marginRight: "8px" }}
                          >
                            {review.task.assigned_user.name?.charAt(0)}
                          </Avatar>
                          <div style={{ fontSize: "0.85rem" }}>
                            {review.task.assigned_user.name}
                          </div>
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          {formatDate(review.created_at)}
                        </small>
                        <Link
                          to={`/tasks/detail/${review.task.id}`}
                          className="btn btn-sm btn-outline-secondary"
                        >
                          Ko'rish
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>

        {/* Menga baho berilgan */}
        <Tab eventKey="for-my-tasks" title={`Menga baho berilgan (${reviewsForMyTasks.length})`}>
          {reviewsForMyTasks.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <Empty description="Sizning tasklaringizga hali baholash yozilmagan" />
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-3">
              {reviewsForMyTasks.map((review) => (
                <Col key={review.id} xs={12} md={6} lg={4}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="mb-3">
                        <Rate disabled value={review.rating} style={{ fontSize: '18px' }} />
                      </div>

                      <h6 className="mb-2">{review.task.title}</h6>

                      {review.review_text && (
                        <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                          "{review.review_text}"
                        </p>
                      )}

                      {review.reviewer && (
                        <div className="d-flex align-items-center mb-2">
                          <Avatar
                            src={review.reviewer.picture}
                            size={28}
                            style={{ marginRight: "8px" }}
                          >
                            {review.reviewer.name?.charAt(0)}
                          </Avatar>
                          <div>
                            <div style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                              {review.reviewer.name}
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                              Baholovchi
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          {formatDate(review.created_at)}
                        </small>
                        <Link
                          to={`/tasks/detail/${review.task.id}`}
                          className="btn btn-sm btn-outline-secondary"
                        >
                          Ko'rish
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>
      </Tabs>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Bahoni tahrirlash</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingReview && (
            <>
              <div className="mb-3">
                <h6>{editingReview.task.title}</h6>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>
                  Reyting (1-5) <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Rate
                    value={editForm.rating}
                    onChange={(value) => setEditForm({ ...editForm, rating: value })}
                    style={{ fontSize: '28px' }}
                    allowClear={false}
                  />
                  {editForm.rating > 0 && (
                    <span className="text-muted">({editForm.rating}/5)</span>
                  )}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Izoh (ixtiyoriy)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Topshiriq haqida fikringizni yozing..."
                  value={editForm.review_text}
                  onChange={(e) => setEditForm({ ...editForm, review_text: e.target.value })}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleCloseEditModal} disabled={submitting}>
            Bekor qilish
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpdateReview}
            disabled={submitting || editForm.rating === 0}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Yuklanmoqda...
              </>
            ) : (
              <>
                <EditOutlined className="me-1" />
                Yangilash
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MyReviewsPage;
